import { useMutation, useQuery } from "@apollo/client";
import Edit from "@mui/icons-material/Edit";
import Refresh from "@mui/icons-material/Refresh";
import { Button, Tooltip, useTheme, useMediaQuery } from "@mui/material";
import {
  GridActionsCellItem,
  GridColumnVisibilityModel,
  GridRowParams,
  GridSortModel,
} from "@mui/x-data-grid-premium";
import { useAlerts } from "components/Alerts/AlertProvider";
import { getFullName } from "encamp-shared/src/utils/name";
import { gql } from "generated-graphql";
import { Permission, UserStatus, UsersQuery } from "generated-graphql/graphql";
import { GridSelectionProvider, OmnisearchDataGridV2, useGridSelection } from "hooks/omnisearch-v2";
import { useAuthorization } from "hooks/useAuthorization";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useCurrentUser } from "hooks/useCurrentUser";
import { FilterKey, OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useTenant } from "hooks/useTenant";
import GET_ROLES_QUERY from "queries/getRoles";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { AddUserDialog } from "./AddUserDialog";
import { BulkReassignRoleDialog } from "./BulkReassignRoleDialog";
import { EditUserDialog } from "./EditUserDialog";
import { prettyPrintUserStatus } from "./util";

export type UserRow = UsersQuery["users"]["items"][number];

// Staff can resend an invite to any user who was invited but has not yet
// completed sign-in. We intentionally don't gate on PENDING alone: the DB
// status can drift (e.g. a tenant admin toggling ACTIVE/INACTIVE on a user who
// never signed in — ENG-15863), which would otherwise hide the recovery path.
// DISABLED users are intentionally deactivated and NOT_INVITED users were never
// sent an invite, so both are excluded. The backend (resendInvitationToUser) is
// the source of truth and returns a clear error if the user is already
// confirmed in Cognito.
const RESENDABLE_USER_STATUSES: UserStatus[] = [
  UserStatus.Pending,
  UserStatus.Active,
  UserStatus.Inactive,
];

const USERS = gql(`
  query Users($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    users(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        id
        email
        status
        facilities {
            id
        }
        person {
          first
          last
        }
        UserTenant {
          tenantId
            role {
                name
                permissions
            }
        }
      }
      count
    }
  }
`);

const RESEND_INVITE = gql(/* GraphQL */ `
  mutation ResendInvite($email: String!) {
    resendInvitationToUser(email: $email)
  }
`);

const INITIAL_SORT_MODEL: GridSortModel = [{ field: "name", sort: "asc" }];

const EXCLUDE_FILTER_COLUMNS = ["numFacilities", "name"];

const ADDITIONAL_FILTER_KEYS: FilterKey[] = [
  { key: "first", header: "First Name" },
  { key: "last", header: "Last Name" },
  { key: "facility", header: "Facility Name", filterKeyType: "facility" },
];

const MOBILE_COLUMN_VISIBILITY: GridColumnVisibilityModel = {
  role: false,
  status: false,
  actions: false,
};

const getItems = (data: UsersQuery) => data.users.items;
const getCount = (data: UsersQuery) => data.users.count;

function buildVariables(
  omnisearch: string,
  pagination: { page: number; pageSize: number },
  sort: GridSortModel
) {
  return {
    search: omnisearch,
    page: pagination.page,
    pageSize: pagination.pageSize,
    sort: sort.flatMap((entry) => {
      if (entry.sort !== "asc" && entry.sort !== "desc") {
        return [];
      }
      return [{ field: entry.field, sort: entry.sort }];
    }),
  };
}

function ReassignRoleButton() {
  const { tenantId } = useTenant();
  const { selectedCount, getSelectedRows, clearSelection } = useGridSelection<UserRow>();
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button variant="outlined" onClick={() => setOpen(true)} disabled={selectedCount === 0}>
        {selectedCount > 0 ? `Reassign role (${selectedCount})` : "Reassign role"}
      </Button>
      {open && tenantId && (
        <BulkReassignRoleDialog
          selectedUsers={getSelectedRows()}
          tenantId={tenantId}
          onClose={() => setOpen(false)}
          onSaved={() => {
            setOpen(false);
            clearSelection();
          }}
        />
      )}
    </>
  );
}

export function Users() {
  return (
    <GridSelectionProvider>
      <UsersGrid />
    </GridSelectionProvider>
  );
}

function UsersGrid() {
  const { tenantId, tenant } = useTenant();
  const { hasPermissions } = useAuthorization();
  const { isStaff } = useCurrentUser();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const canViewUsersList = hasPermissions([Permission.ReadAllUserTenant]);
  const canReassignRole = hasPermissions([Permission.WriteAllUserTenant]);
  const canAddUser = hasPermissions([Permission.CreateUserTenant]);
  const navigate = useNavigate();
  if (!canViewUsersList) {
    navigate("/");
  }

  const [addUser, setAddUser] = useState<boolean>(false);
  const [editUser, setEditUser] = useState<UserRow | null>(null);

  // Fuel the role filter dropdown with this tenant's actual roles.
  const { data: rolesData } = useQuery(GET_ROLES_QUERY, {
    variables: { tenantId: tenantId ?? "" },
    skip: !tenantId,
  });
  const roleNames = useMemo(
    () => (rolesData?.roles ?? []).map((r) => r.name).sort((a, b) => a.localeCompare(b)),
    [rolesData?.roles]
  );

  useBreadcrumb([
    { label: tenant?.name ?? "Unknown Tenant" },
    { label: "Account" },
    { label: "Users" },
  ]);
  const alerts = useAlerts();
  const [resendInvite] = useMutation(RESEND_INVITE, {
    onError: (error) => alerts.error(error.message),
    onCompleted: (result) => {
      if (result.resendInvitationToUser) {
        alerts.success("Invite sent successfully.");
      }
    },
  });

  const columns: OmnisearchGridColDef<UserRow>[] = useMemo(
    () => [
      {
        field: "name",
        headerName: "Name",
        flex: isMobile ? 0.4 : 2,
        sortable: true,
        valueGetter: (_, row) => getFullName(row.person),
      },
      {
        field: "email",
        headerName: "Email",
        flex: isMobile ? 0.45 : 2,
        sortable: true,
      },
      {
        field: "numFacilities",
        headerName: "Facilities",
        flex: isMobile ? 0.15 : 1,
        sortable: true,
        valueGetter: (_, row) => {
          const role = row.UserTenant.find((ut) => ut.tenantId === tenantId)?.role;

          if (
            role?.permissions?.includes(Permission.ReadAllFacility) ||
            role?.permissions?.includes(Permission.WriteAllFacility)
          )
            return "all";

          return row.facilities.length;
        },
      },
      {
        field: "role",
        headerName: "Role",
        flex: 2,
        sortable: true,
        filterKeyType: "enumAutocomplete",
        enumValues: roleNames,
        enumPresentationFunction: (v) => v,
        valueGetter: (_, row) =>
          row.UserTenant?.map((userTenant) => userTenant.role?.name).join(", "),
      },
      {
        field: "status",
        headerName: "Status",
        flex: 2,
        sortable: true,
        filterKeyType: "enumAutocomplete",
        enumValues: Object.values(UserStatus),
        enumPresentationFunction: (v) => prettyPrintUserStatus(v as UserStatus),
        valueGetter: (_, row) => prettyPrintUserStatus(row.status),
      },
      {
        field: "actions",
        type: "actions",
        minWidth: 80,
        flex: 0.5,
        align: "right",
        getActions: ({ row }) => {
          const actions = [];

          if (isStaff && RESENDABLE_USER_STATUSES.includes(row.status)) {
            actions.push(
              <Tooltip title="Resend Invite" key={1}>
                <GridActionsCellItem
                  onClick={() => resendInvite({ variables: { email: row.email } })}
                  label="Resend Invite"
                  icon={<Refresh />}
                />
              </Tooltip>
            );
          }
          if (canReassignRole) {
            actions.push(
              <Tooltip title="Edit User" key={2}>
                <GridActionsCellItem
                  onClick={() => setEditUser(row)}
                  label="Edit User"
                  icon={<Edit />}
                />
              </Tooltip>
            );
          }

          return actions;
        },
      },
    ],
    [isMobile, tenantId, isStaff, canReassignRole, resendInvite, roleNames]
  );

  const commandButtons = useMemo(() => {
    const buttons = [];
    if (canReassignRole) {
      buttons.push(<ReassignRoleButton key="reassign" />);
    }
    if (canAddUser) {
      buttons.push(
        <Button key="add" variant="contained" onClick={() => setAddUser(true)}>
          Add User
        </Button>
      );
    }
    return buttons;
  }, [canReassignRole, canAddUser]);

  const handleRowClick = useCallback((params: GridRowParams<UserRow>) => {
    setEditUser(params.row);
  }, []);

  return (
    <>
      {addUser && (
        <AddUserDialog open={addUser} onClose={() => setAddUser(false)} tenantId={tenantId ?? ""} />
      )}
      {editUser && (
        <EditUserDialog
          open={!!editUser}
          initialUserId={editUser.id}
          onClose={() => setEditUser(null)}
          tenantId={tenantId ?? ""}
        />
      )}

      <OmnisearchDataGridV2
        query={USERS}
        persistenceKey="users-table"
        columns={columns}
        getItems={getItems}
        getCount={getCount}
        buildVariables={buildVariables}
        initialSortModel={INITIAL_SORT_MODEL}
        initialPageSize={50}
        excludeFilterColumns={EXCLUDE_FILTER_COLUMNS}
        additionalFilterKeys={ADDITIONAL_FILTER_KEYS}
        mobileColumnVisibilityModel={MOBILE_COLUMN_VISIBILITY}
        commandButtons={commandButtons}
        checkboxSelection={canReassignRole}
        disableRowSelectionOnClick
        onRowClick={handleRowClick}
        noRowsMessage="No users have been added to this organization yet."
        tenantId={tenantId}
      />
    </>
  );
}
